import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, ChevronRight, ChevronLeft, Home, Check, Calculator } from 'lucide-react';

const homeTypes = [
  { id: '2bhk', label: '2 BHK', range: '\u20b94-8 Lakhs' },
  { id: '3bhk', label: '3 BHK', range: '\u20b96-12 Lakhs' },
  { id: '4bhk', label: '4 BHK', range: '\u20b98-18 Lakhs' },
  { id: 'villa', label: 'Villa', range: '\u20b912-30+ Lakhs' },
];

const rooms = [
  { id: 'kitchen', label: 'Kitchen', icon: '\ud83c\udf73' },
  { id: 'wardrobes', label: 'Wardrobes', icon: '\ud83d\udc54' },
  { id: 'living', label: 'Living Room', icon: '\ud83d\udecb\ufe0f' },
  { id: 'bedroom', label: 'Bedrooms', icon: '\ud83d\udecf\ufe0f' },
  { id: 'dining', label: 'Dining', icon: '\ud83c\udf7d\ufe0f' },
  { id: 'study', label: 'Study', icon: '\ud83d\udcda' },
  { id: 'pooja', label: 'Pooja Room', icon: '\ud83d\ude4f' },
  { id: 'other', label: 'Other', icon: '\u2728' },
];

const finishes = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'Quality materials, functional designs',
    multiplier: 1,
  },
  {
    id: 'premium',
    label: 'Premium',
    description: 'Enhanced finishes, designer hardware',
    multiplier: 1.4,
  },
  {
    id: 'luxury',
    label: 'Luxury',
    description: 'Top-tier materials, bespoke designs',
    multiplier: 2,
  },
];

const contactSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  phone: z.string().min(10, 'Valid phone number required'),
  locality: z.string().min(2, 'Locality is required'),
});

export default function BudgetEstimator({ onClose }) {
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({
    homeType: '',
    rooms: [],
    finish: '',
  });
  const [estimate, setEstimate] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const calculateEstimate = () => {
    const baseRanges = {
      '2bhk': { min: 400000, max: 800000 },
      '3bhk': { min: 600000, max: 1200000 },
      '4bhk': { min: 800000, max: 1800000 },
      'villa': { min: 1200000, max: 3000000 },
    };

    const base = baseRanges[selections.homeType] || { min: 500000, max: 1000000 };
    const roomFactor = 0.6 + (selections.rooms.length * 0.1);
    const finishMultiplier = finishes.find((f) => f.id === selections.finish)?.multiplier || 1;

    const min = Math.round((base.min * roomFactor * finishMultiplier) / 100000) * 100000;
    const max = Math.round((base.max * roomFactor * finishMultiplier) / 100000) * 100000;

    setEstimate({ min, max });
    setStep(4);
  };

  const onSubmit = (data) => {
    // Placeholder for API integration
    console.log('Form submitted:', { ...selections, ...data, estimate });
    setIsSubmitted(true);
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `\u20b9${(amount / 10000000).toFixed(1)} Cr`;
    }
    return `\u20b9${(amount / 100000).toFixed(1)} Lakhs`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-primary/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-large overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-brand p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <Calculator className="w-10 h-10 mb-3" />
          <h3 className="font-heading text-2xl">Budget Estimator</h3>
          <p className="text-white/80 text-sm mt-1">
            Get a realistic range in 30 seconds
          </p>

          {/* Progress */}
          <div className="flex gap-2 mt-6">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Home Type */}
          {step === 1 && (
            <div>
              <h4 className="font-heading text-lg text-brand-primary mb-4">
                What type of home?
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {homeTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelections({ ...selections, homeType: type.id })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selections.homeType === type.id
                        ? 'border-brand-accent bg-brand-accent/5'
                        : 'border-border-light hover:border-brand-accent/50'
                    }`}
                  >
                    <Home
                      className={`w-6 h-6 mb-2 ${
                        selections.homeType === type.id
                          ? 'text-brand-accent'
                          : 'text-text-secondary'
                      }`}
                    />
                    <p className="font-medium text-brand-primary">{type.label}</p>
                    <p className="text-xs text-text-secondary">{type.range}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => selections.homeType && setStep(2)}
                disabled={!selections.homeType}
                className="w-full mt-6 btn-primary disabled:opacity-50"
              >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}

          {/* Step 2: Rooms */}
          {step === 2 && (
            <div>
              <h4 className="font-heading text-lg text-brand-primary mb-4">
                Which rooms need interiors?
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => {
                      const newRooms = selections.rooms.includes(room.id)
                        ? selections.rooms.filter((r) => r !== room.id)
                        : [...selections.rooms, room.id];
                      setSelections({ ...selections, rooms: newRooms });
                    }}
                    className={`p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selections.rooms.includes(room.id)
                        ? 'border-brand-accent bg-brand-accent/5'
                        : 'border-border-light hover:border-brand-accent/50'
                    }`}
                  >
                    <span className="text-2xl">{room.icon}</span>
                    <span className="font-medium text-brand-primary text-sm">
                      {room.label}
                    </span>
                    {selections.rooms.includes(room.id) && (
                      <Check className="w-4 h-4 text-brand-accent ml-auto" />
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 btn-secondary"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <button
                  onClick={() => selections.rooms.length > 0 && setStep(3)}
                  disabled={selections.rooms.length === 0}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  Continue
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Finish Level */}
          {step === 3 && (
            <div>
              <h4 className="font-heading text-lg text-brand-primary mb-4">
                Choose your finish level
              </h4>
              <div className="space-y-3">
                {finishes.map((finish) => (
                  <button
                    key={finish.id}
                    onClick={() => setSelections({ ...selections, finish: finish.id })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selections.finish === finish.id
                        ? 'border-brand-accent bg-brand-accent/5'
                        : 'border-border-light hover:border-brand-accent/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-brand-primary">{finish.label}</p>
                        <p className="text-sm text-text-secondary">{finish.description}</p>
                      </div>
                      {selections.finish === finish.id && (
                        <Check className="w-5 h-5 text-brand-accent" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 btn-secondary"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <button
                  onClick={calculateEstimate}
                  disabled={!selections.finish}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  Get Estimate
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Result & Contact */}
          {step === 4 && !isSubmitted && (
            <div>
              <div className="bg-brand-accent/10 rounded-2xl p-6 mb-6 text-center">
                <p className="text-sm text-text-secondary mb-2">Estimated Range</p>
                <p className="text-3xl font-heading text-brand-primary">
                  {formatCurrency(estimate?.min)} - {formatCurrency(estimate?.max)}
                </p>
                <p className="text-xs text-text-secondary mt-2">
                  *Actual cost depends on specific requirements
                </p>
              </div>

              <p className="text-sm text-text-secondary mb-4">
                Get a detailed, personalized quote from our design experts:
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <input
                    {...register('name')}
                    placeholder="Your Name"
                    className="form-input"
                  />
                  {errors.name && (
                    <p className="form-error">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <input
                    {...register('phone')}
                    placeholder="WhatsApp Number"
                    className="form-input"
                  />
                  {errors.phone && (
                    <p className="form-error">{errors.phone.message}</p>
                  )}
                </div>
                <div>
                  <input
                    {...register('locality')}
                    placeholder="Locality (e.g., Gachibowli)"
                    className="form-input"
                  />
                  {errors.locality && (
                    <p className="form-error">{errors.locality.message}</p>
                  )}
                </div>
                <button type="submit" className="w-full btn-primary">
                  Get Detailed Quote
                </button>
              </form>
            </div>
          )}

          {/* Success State */}
          {isSubmitted && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-heading text-xl text-brand-primary mb-2">
                Thank you!
              </h4>
              <p className="text-text-secondary mb-6">
                Our design expert will contact you within 24 hours with a detailed quote.
              </p>
              <button onClick={onClose} className="btn-primary">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
